import { useQuery } from '@tanstack/react-query';
import type { ItemResponse, SearchResult } from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';
import type { SearchResourceTypeUi } from './searchMeta';

/**
 * Universelle Suche (ADR 0015/0069). Liefert nach Typ gruppierte Treffer. Die Query
 * ist nur aktiv, wenn ein Suchbegriff vorliegt — leere Eingaben fragen nicht an.
 */
export function useSearch(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['search', trimmed],
    queryFn: () =>
      apiRequest<ItemResponse<SearchResult>>(`/v1/search?q=${encodeURIComponent(trimmed)}`),
    enabled: trimmed.length >= 1,
    staleTime: 30_000,
  });
}

/**
 * Zuordnung Ressourcentyp → Ziel im Frontend (Routen bleiben UI-Sache).
 *
 * Bewusst ein `Record` über ALLE Typen: Ein neuer Ressourcentyp bricht damit den
 * Typecheck, statt still als toter Link zu enden. Typen ohne eigene Detailseite
 * zeigen auf ihre Liste — lieber eine Seite weiter klicken als ins Leere.
 *
 * Erwartete `id` je Typ ist immer der Primärschlüssel des Datensatzes; zwei
 * Ausnahmen, weil die Route etwas anderes adressiert:
 * - `assembly-report` → die **Montageauftrags-ID** (der Bericht hängt 1:1 daran,
 *   `AssemblyReport.assemblyTaskId` ist `@unique`).
 * - `material-list` → die **Projekt-ID**. Eine Bedarfsliste ist nur unter ihrem
 *   Projekt adressierbar (`/materialbedarf/<projektId>`); das Backend liefert
 *   deshalb bewusst die Projekt-ID und dedupliziert mehrere Listen desselben
 *   Projekts — auf der Zielseite stehen ohnehin alle Listen des Projekts.
 */
const FEATURE_ROUTE: Record<SearchResourceTypeUi, (id: string) => string> = {
  project: (id) => `/projekte/${id}`,
  customer: (id) => `/crm/${id}`,
  // Die Verzeichnisseite leitet `?open=` ohnehin nur auf die Detailseite um.
  contact: (id) => `/crm/ansprechpartner/${id}`,
  article: (id) => `/einkauf/${id}`,
  'commodity-group': () => '/engineering/warengruppen',
  'sales-document': (id) => `/verkauf/${id}`,
  'purchase-order': (id) => `/beschaffung/${id}`,
  'production-order': () => '/montage',
  'assembly-task': (id) => `/montage?open=${id}`,
  maintenance: (id) => `/montage?tab=wartungen&open=${id}`,
  payment: () => '/buchhaltung',
  employee: (id) => `/hr?tab=mitarbeiter&open=${id}`,
  position: () => '/hr?tab=positionen',
  'dms-document': (id) => `/dateien?doc=${id}`,
  'delivery-address': (id) => `/crm/lieferadressen/${id}`,
  transport: (id) => `/transporte/${id}`,
  'blocked-stock': (id) => `/sperrlager/${id}`,
  'qm-process': (id) => `/qm/${id}`,
  'material-list': (id) => `/materialbedarf/${id}`,
  forecast: (id) => `/bedarfsvorschau/${id}`,
  'goods-receipt': (id) => `/wareneingang/${id}`,
  'picking-order': (id) => `/kommissionierung/${id}`,
  'drawing-number': () => '/engineering/zeichnungsnummern',
  'material-code': () => '/engineering/materialcodes',
  feedback: () => '/verbesserungen',
  'sales-pipeline': () => '/vertriebspipeline',
  'inventory-count': (id) => `/einkauf/inventur/${id}`,
  'assembly-report': (id) => `/montage/${id}/bericht`,
  'legal-entity': () => '/einstellungen',
  // Benutzerkonten stehen bei den Personen (HR + Login zusammengeführt). Der
  // `?open=`-Sprung dort erwartet eine Mitarbeiter-ID, nicht die Benutzer-ID —
  // deshalb bewusst nur die Liste.
  user: () => '/hr?tab=mitarbeiter',
};

/** Detail-/Zielroute eines Treffers. Unbekannte Typen landen auf der Ergebnisseite. */
export function featureRoute(type: SearchResourceTypeUi, id: string): string {
  const build = FEATURE_ROUTE[type];
  return build ? build(id) : '/suche';
}
